import React from 'react';
import { AlertTriangle, CheckCircle2, ChevronRight, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDeck } from './store';
import { SLIDES } from './slides';
import { Section, countErrors, runChecklist } from './checks';

function SectionBlock({ s, onGo }: { s: Section; onGo: (i: number) => void }) {
  const errors = s.items.filter((i) => i.level === 'error').length;
  const warns = s.items.length - errors;
  return (
    <div className="rounded-xl border border-neutral-200 p-3" data-testid={`checklist-section-${s.id}`}>
      <div className="flex items-center gap-2">
        {errors ? <XCircle size={17} className="text-red-600" /> : warns ? <AlertTriangle size={17} className="text-amber-500" /> : <CheckCircle2 size={17} className="text-emerald-600" />}
        <h3 className="font-app text-[15px] font-semibold">{s.title}</h3>
        <span className="ml-auto text-xs text-neutral-500" data-testid={`checklist-count-${s.id}`}>{errors ? `${errors} à corriger` : ''}{errors && warns ? ' · ' : ''}{warns ? `${warns} à vérifier` : ''}</span>
      </div>
      {s.items.length === 0 ? (
        <p className="mt-1 pl-6 text-sm text-emerald-700">{s.ok}</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-0.5">
          {s.items.map((it, i) => (
            <li key={`${it.slide}-${it.label}`}>
              <button onClick={() => onGo(it.slide)} data-testid={`checklist-item-${s.id}-${i}`} className="group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-neutral-100 transition-colors">
                <span className={`h-2 w-2 shrink-0 rounded-full ${it.level === 'error' ? 'bg-red-600' : 'bg-amber-400'}`} />
                <span className="flex-1">{it.label}</span>
                <span className="shrink-0 text-xs text-neutral-400">Slide {SLIDES[it.slide]?.n}</span>
                <ChevronRight size={14} className="shrink-0 text-neutral-400 transition-transform group-hover:translate-x-0.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function useChecklist() {
  const { get, team } = useDeck();
  const sections = runChecklist(get, team);
  return { sections, errors: countErrors(sections) };
}

export function ChecklistDialog({ open, onClose, go }: { open: boolean; onClose: () => void; go: (i: number) => void }) {
  const { sections, errors } = useChecklist();
  const warns = sections.reduce((n, s) => n + s.items.length, 0) - errors;
  const onGo = (i: number) => { onClose(); go(i); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-auto font-ui" data-testid="checklist-dialog">
        <DialogHeader>
          <DialogTitle className="font-app">Vérification avant l'oral</DialogTitle>
          <DialogDescription>Cliquez sur une ligne pour aller directement à la slide concernée.</DialogDescription>
        </DialogHeader>
        <div className={`rounded-xl px-4 py-3 font-app text-[15px] font-semibold ${errors ? 'bg-red-50 text-red-700' : warns ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`} data-testid="checklist-summary">
          {errors ? `${errors} point${errors > 1 ? 's' : ''} à corriger` : warns ? 'Presque prêt' : "Prêt pour l'oral !"}
          {warns > 0 && <span className="font-ui text-sm font-normal"> · {warns} à vérifier</span>}
        </div>
        <div className="flex flex-col gap-3">
          {sections.map((s) => <SectionBlock key={s.id} s={s} onGo={onGo} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
